import { getWorkflowStepDependencies } from '../getWorkflowStepDependencies.js';
import { getArgumentValue } from '../getArgumentValue.js';
import { validateConditional } from './validateConditional.js';
import { validateAdvancedFilters } from './validateAdvancedFilters.js';
import { getMissingRequiredArguments } from '../../utils/getMissingRequiredArguments.js';

const validateCredentials = ({ app, step, workflow, credential, status }) => {
  const authType = app?.authentication?.[0]?.type;
  const authTypeIsNone = authType === 'none';

  if (authTypeIsNone) {
    return { status: 'valid' };
  }

  // Integration workflows resolve connections per installation at run time: the embedded
  // app through the credential exchange, connector apps through the integration-app
  // binding the editor manages itself. There is no company credential to "select", and
  // the embedded app legitimately carries neither field, so the check below flagged every
  // one of its steps and pointed at a Connect tab the editor does not render for it.
  if (workflow?.integrationId) {
    return { status: 'valid' };
  }

  // "By environment" and conditional connections pick the connection at run time (getEffectiveStepCredentialId).
  if (step?.options?.connectionType === 'environment' || step?.options?.conditionalAuth?.length > 0) {
    return { status: 'valid' };
  }

  if (!step?.credentialId && !step?.integrationAppId) {
    return { status, message: 'Select auth' };
  }

  if (credential?.status === 'invalid') {
    return { status: 'invalid', message: 'Fix auth' };
  }

  return { status: 'valid' };
};

const validateRequiredArgs = ({ app, step, status }) => {
  const actionDefinition = app?.actions?.find((a) => a.id === step?.actionId);
  const stepArgs = step?.arguments || {};

  const missingRequiredArguments = getMissingRequiredArguments({ definitions: actionDefinition?.arguments, values: stepArgs });

  if (missingRequiredArguments.length > 0) {
    return { status, message: `Fill in required fields` };
  }

  return { status: 'valid' };
};

const validateOptions = async ({ options, status }) => {
  if (!options || (options?.filters?.length === 0 && options?.sortCriteria?.length === 0 && options?.slices?.length === 0)) {
    return { status: 'valid' };
  }

  for (const filter of options?.filters || []) {
    const validation = await validateConditional({ condition: filter, name: 'filter', status });
    if (validation.status !== 'valid') {
      return validation;
    }
  }

  if (options?.advancedFilters?.filterSet?.length > 0) {
    const validation = await validateAdvancedFilters({ conditions: options?.advancedFilters, name: 'filter', status });
    if (validation.status !== 'valid') {
      return validation;
    }
  }

  if (options?.sort?.length > 0) {
    for (const sort of options?.sort) {
      if (!sort?.field) {
        return { status, message: 'Add sort field' };
      }
      if (!sort?.value) {
        return { status, message: 'Add sort value' };
      }
    }
  } else {
    for (const sort of options?.sortCriteria || []) {
      if (!sort?.field) {
        return { status, message: 'Add sort field' };
      }
      if (!sort?.value) {
        return { status, message: 'Add sort value' };
      }
    }
  }

  for (const slice of options?.slices || []) {
    const [from, to] = await Promise.all([
      getArgumentValue({ arg: slice?.from, opts: { format: 'human' } }), //
      getArgumentValue({ arg: slice?.to, opts: { format: 'human' } })
    ]);

    if (!from) {
      return { status, message: 'Add slice start' };
    }
    if (!to) {
      return { status, message: 'Add slice end' };
    }
  }

  return { status: 'valid' };
};

export const validateApp = async ({ app, step, workflow, credential, status }) => {
  if (step?.functionId) {
    // TODO: validate functionInput steps.
    // You should go through every one of the function's steps and check if they are valid, then validate the arguments.
    return { status: 'valid' };
  }

  if (!app || app?.slug === 'appSelector') {
    return { status: 'setup', message: 'Select an app' };
  }

  if (app?.slug === 'triggerSelector' || app?.slug === 'appTriggerSelector') {
    return { status: 'setup', message: 'Select a trigger' };
  }

  if (app?.slug === 'runWorkflow') {
    return { status: 'setup', message: 'Select a workflow' };
  }

  const credentialValidation = validateCredentials({ app, step, workflow, credential, status });
  if (credentialValidation?.status !== 'valid') {
    return { ...credentialValidation, id: 'go-to-credentials' };
  }

  const hasActions = app?.actions?.length > 0;
  if (hasActions && !step?.actionId) {
    return { status, id: 'go-to-config', message: 'Select an action' };
  }

  const dependencies = getWorkflowStepDependencies(step);
  const hasMissingDependencies = dependencies.some((dependency) => !workflow?.steps?.find((s) => s?.id === dependency));
  if (hasMissingDependencies) {
    // return { status: 'invalid', id: 'go-to-config', message: 'Fix dependencies' };
  }

  const requiredArgsValidation = validateRequiredArgs({ app, step, status });
  if (requiredArgsValidation?.status !== 'valid') {
    return { ...requiredArgsValidation, id: 'go-to-config' };
  }

  const optionsValidation = await validateOptions({ options: step?.options, status });
  if (optionsValidation?.status !== 'valid') {
    return { ...optionsValidation, id: 'go-to-config' };
  }

  return { status: 'valid' };
};
